import cx from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { DOMAINS_WITH_USER_ACCOUNT } from '@js/common/constants'
import { UA } from '@js/common/UA'
import User from '@js/common/user'
import { getTestParam, isClientSide } from '@js/common/utils'

import CTA from '@f/elements/CTA/CTA'
import Icon from '@f/elements/Icon/Icon'

import { Domain } from '@f-fertighaus/applications/Next/pages/house-detail-page/HDPContent'
import useOutsideAlerter from '@f-fertighaus/applications/SalesAgentDashboard/hooks/useOutsideAlerter'
import { ClientOnly } from '@f-fertighaus/components/ClientOnly/ClientOnly'
import SiteSearchNav from '@f-fertighaus/widgets/SiteSearchNav/SiteSearchNav'

import { PageHeader } from './components'
import { SiteSearchFallback } from './components/SiteSearch'
import { UserMenu } from './components/UserMenu'
import { MenuItemType, usePageHeader } from './PageHeaderSSR'

type DomainBasedCTA = {
  [key in Domain]: {
    href: string
    label: string
  }
}

const quizCTADictionary: DomainBasedCTA = {
  fertighaus: {
    href: '/hausbau-assistent/',
    label: 'Hausbau-Assistent'
  },
  massivhaus: {
    href: '/hausbau-quiz/',
    label: 'Hausbau-Quiz'
  },
  bungalow: {
    href: '/kataloganfrage/',
    label: 'Kataloge anfordern'
  },
  einfamilienhaus: {
    href: '/hausbau-assistent/',
    label: 'Hausbau-Assistent'
  },
  bauen: {
    href: '/bestellungen/kataloge/',
    label: 'Hauskataloge'
  },
  immowelt: {
    href: '/bestellungen/kataloge/',
    label: 'Hauskataloge'
  },
  immonet: {
    href: '/bestellungen/kataloge/',
    label: 'Hauskataloge'
  }
}

const partnershipCTADictionary: DomainBasedCTA = {
  fertighaus: {
    href: '/baupartner-werden/',
    label: 'Baupartner werden'
  },
  massivhaus: {
    href: '/baupartner-anmelden/',
    label: 'Baupartner anmelden'
  },
  bungalow: {
    href: '/baupartner-anmelden/',
    label: 'Baupartner anmelden'
  },
  einfamilienhaus: {
    href: '/baupartner-werden/',
    label: 'Baupartner werden'
  },
  bauen: {
    href: '/baupartner-anmelden/',
    label: 'Baupartner werden'
  },
  immowelt: {
    href: '/baupartner-anmelden/',
    label: 'Baupartner werden'
  },
  immonet: {
    href: '/baupartner-anmelden/',
    label: 'Baupartner werden'
  }
}

export type handleMenuItemClickType = (
  menuItem: MenuItemType,
  forceOpen?: boolean
) => void

export const PageHeaderStandard = () => {
  const [
    currentActiveMenu,
    setCurrentActiveMenu
  ] = useState<null | MenuItemType>(null)
  const { menu_items: menuItems, pagetype, domain } = usePageHeader()
  const [keepActiveMenuOpen, setKeepActiveMenuOpen] = useState(false)
  const isBVersionTest =
    domain === 'bauen' && getTestParam('NR_11222_BN_HLP_Quiz_Entry') === 'B'

  const BVersionTestCTA = {
    href: '/bestellungen/kataloge/',
    label: 'Bauprojekt-Quiz'
  }

  const [postcode, setPostcode] = useState(
    isClientSide ? User.getLocation().postcode : undefined
  )
  const pageHeaderWrapper = isClientSide && window.mountHeader
  const quizCTA = isBVersionTest ? BVersionTestCTA : quizCTADictionary[domain]
  const partnershipCTA = partnershipCTADictionary[domain]

  useEffect(() => {
    const token = PubSub.subscribe('QUERYUPDATE', () => {
      setPostcode(User.getLocation().postcode)
    })

    return () => {
      PubSub.unsubscribe(token)
    }
  }, [])

  const wrapperRef = useRef<HTMLDivElement | null>(null)

  useOutsideAlerter(wrapperRef, () => {
    setCurrentActiveMenu(null)
  })

  const handleMenuItemClick: handleMenuItemClickType = (
    menuItem,
    forceOpen = false
  ) => {
    setCurrentActiveMenu(menuItem)
    setKeepActiveMenuOpen(forceOpen)
  }

  const closeAllMenus = useCallback(
    (props: { forceCloseCurrentMenu?: boolean }) => {
      if (keepActiveMenuOpen && !props?.forceCloseCurrentMenu) {
        return
      }
      setCurrentActiveMenu(null)
    },
    [keepActiveMenuOpen]
  )

  const checkForEscKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        if (currentActiveMenu !== null && currentActiveMenu !== 'search') {
          setCurrentActiveMenu(null)
        }
      }
    },
    [currentActiveMenu]
  )

  useEffect(() => {
    document.addEventListener('keyup', checkForEscKeyPress)
    const headerElement = document.querySelector('#ssr-header')

    headerElement?.addEventListener('mouseleave', closeAllMenus)

    return () => {
      document.removeEventListener('keyup', checkForEscKeyPress)
      headerElement?.removeEventListener('mouseleave', closeAllMenus)
    }
  }, [currentActiveMenu, checkForEscKeyPress, closeAllMenus])

  useEffect(() => {
    if (pageHeaderWrapper) {
      if (currentActiveMenu) {
        pageHeaderWrapper.classList.add('is-toggled')
      } else {
        pageHeaderWrapper.classList.remove('is-toggled')
      }
    }
  }, [currentActiveMenu, pageHeaderWrapper])

  if (isClientSide && /^\/servicewelt-bauen\//.test(window.location.pathname)) {
    return null
  }

  const isResultsPage = pagetype === 'full-text-search'
  const isFertighaus = domain === 'fertighaus'

  const propsForChildren = {
    currentActiveMenu,
    keepActiveMenuOpen,
    handleMenuItemClick,
    closeAllMenus
  }

  return (
    <div
      ref={wrapperRef}
      className={cx(
        'js-page-header',
        '[ f-d-flr f-fl-jc-sb f-fl-al-ce ]',
        'PageHeader-navigation'
      )}>
      <PageHeader.DesktopNav
        onClick={handleMenuItemClick}
        closeAllMenus={closeAllMenus}
        currentActiveMenu={currentActiveMenu}
        keepActiveMenuOpen={keepActiveMenuOpen}
        postcode={postcode}
      />
      <div className='[ f-d-flr f-fl-al-ce ] PageHeader-buttons'>
        {!UA.isLoggedIn() && (
          <ClientOnly>
            <CTA
              tag='a'
              type='block'
              className='[ f-t-body f-p-v-010 f-p-h-020 f-t-ellip ] PageHeader-CTA-block'
              href={quizCTA.href}
              wtData={{
                'CTA-click': {
                  location: 'header',
                  label: 'vendor-quiz-standalone',
                  indicates: ['funnel-entry']
                }
              }}>
              {quizCTA.label}
            </CTA>
          </ClientOnly>
        )}
        {isFertighaus && (
          <ClientOnly
            fallback={
              <SiteSearchFallback
                currentActiveMenu={currentActiveMenu}
                isResultsPage={isResultsPage}
              />
            }>
            <SiteSearchNav {...propsForChildren} resultsPage={isResultsPage} />
          </ClientOnly>
        )}
        {DOMAINS_WITH_USER_ACCOUNT.includes(domain) && (
          <UserMenu {...propsForChildren} />
        )}
        <CTA
          tag='a'
          type='block-tertiary'
          href={partnershipCTA.href}
          className={`[ f-p-r-030:l f-m-v-010 f-p-a-010 f-hidden:s f-hidden:m ] PageHeader-button PageHeader-additional-button`}
          data-sel='btn-nav-prtn'
          wtData={{
            'CTA-click': {
              location: 'header'
            }
          }}>
          <Icon
            icon='briefcase-16px'
            className={`[ f-m-r-005 f-c-st-inherit ] PageHeader-button-icon PageHeader-icon`}
          />
          <span className='[ f-hidden:s f-hidden:m f-c-st-inherit ] PageHeader-button-label'>
            {partnershipCTA.label.split(' ')[0]}
          </span>
        </CTA>
        {menuItems && (
          <PageHeader.MobileNav
            {...propsForChildren}
            quizCTA={quizCTA}
            postcode={postcode}
            partnershipCTA={partnershipCTA}
            menuItems={menuItems}
          />
        )}
      </div>
    </div>
  )
}
